import {
  ListPayeeRequirementsFilterParameter,
  PayeeRequirement,
  RequirementExpirationStatus,
  RequirementStatus,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import flatten from "lodash/flatten";
import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { queriesPayees, queriesRequirements } from "@query";
import { useXWingspanPayerId } from "../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import {
  UrlQuery,
  UrlQueryFilter,
  useUrlQuery
} from "../../../shared/hooks/useUrlQuery";
import { useSetWSStore } from "../../../store";
import { PayeeFilterRequirementStatus } from "../../Payees/routes/RoutePayees/filters/types";
import {
  getRequirementDefinitionIcon,
  getRequirementDefinitionName
} from "../../RequirementDefinitions/utils";
import { RequirementContextType } from "../../Requirements/types";
import { useRequirementsConfig } from "../../Requirements/useRequirementsConfig";
import { PayeeName } from "../components/PayeeName/PayeeName";
import { BulkReviewProps } from "../types";

const PAGE_SIZE = 20;

interface RequirementsUrlQueryFilter extends UrlQueryFilter {
  requirementDefinitionId?: string;
  status?: PayeeFilterRequirementStatus;
}

export type RequirementsUrlQuery = UrlQuery<RequirementsUrlQueryFilter>;

function statusToRequest(
  value?: PayeeFilterRequirementStatus
): ListPayeeRequirementsFilterParameter {
  switch (value) {
    case PayeeFilterRequirementStatus.AwaitingContractorSignature:
      return {
        type: { in: [RequirementType.SIGNATURE] },
        // TODO(v3-verify): dataSourceMetadata is an untyped `{[k:string]: any}`
        // bag on v3 (spec §1) — carrying the v1 vendor-status wire values
        // ("New"/"Sent") forward unverified against the v3 data source.
        dataSourceMetadata_status: { in: ["New", "Sent"] }
      };
    case PayeeFilterRequirementStatus.AwaitingClientSignature:
      return {
        type: { in: [RequirementType.SIGNATURE] },
        dataSourceMetadata_status: { in: ["Pending"] }
      };
    case PayeeFilterRequirementStatus.PendingBackgroundCheck:
      return {
        type: { in: [RequirementType.BACKGROUND_CHECK] },
        dataSourceMetadata_status: { in: ["New", "Pending"] }
      };
    case PayeeFilterRequirementStatus.ProcessingBackgroundCheck:
      return {
        type: { in: [RequirementType.BACKGROUND_CHECK] },
        dataSourceMetadata_status: { in: ["Processing"] }
      };
    case PayeeFilterRequirementStatus.FailedBackgroundCheck:
      return {
        type: { in: [RequirementType.BACKGROUND_CHECK] },
        dataSourceMetadata_status: { in: ["Failed"] }
      };
    case PayeeFilterRequirementStatus.Incomplete:
      return { status: { in: [RequirementStatus.PENDING_COMPLETION] } };
    case PayeeFilterRequirementStatus.Complete:
      return { status: { in: [RequirementStatus.COMPLETED] } };
    case PayeeFilterRequirementStatus.PendingApproval:
      return { status: { in: [RequirementStatus.PENDING_PAYER_REVIEW] } };
    case PayeeFilterRequirementStatus.Rejected:
      return {
        payerOverrideStatus: { in: [RequirementStatus.PENDING_COMPLETION] }
      };
    case PayeeFilterRequirementStatus.Approved:
      return { payerOverrideStatus: { in: [RequirementStatus.COMPLETED] } };
    case PayeeFilterRequirementStatus.Expired:
      return {
        expirationStatus: { in: [RequirementExpirationStatus.EXPIRED] }
      };
    case PayeeFilterRequirementStatus.Expiring:
      return {
        expirationStatus: { in: [RequirementExpirationStatus.EXPIRING] }
      };
    default:
      return {};
  }
}

const createListRequest = (
  urlQuery: RequirementsUrlQuery
): { filter: ListPayeeRequirementsFilterParameter } => {
  return {
    filter: {
      ...statusToRequest(urlQuery.filter.status),
      ...(urlQuery.filter.requirementDefinitionId
        ? {
            requirementDefinitionId: {
              in: [urlQuery.filter.requirementDefinitionId]
            }
          }
        : {})
      // TODO(v3-blocker): v1 filtered on `payerPayeeEngagementIds: { "!=": "null" }`
      // (only requirements attached to a payee engagement) — v3's
      // `ListPayeeRequirementsFilterParameter` has no existence/null operator for
      // `payeeEngagementId`, so this constraint is dropped (accepted breakage).
    }
    // TODO(v3-verify): server order unspecified — v1 sorted
    // updatedAt/expirationDate desc; v3 `listPayeeRequirements` has no sort param.
  };
};

export function useBulkReviewRequirements(requirementId?: string) {
  const history = useHistory();
  const setStore = useSetWSStore();
  const location = useLocation();
  const xWingspanAccount = useXWingspanPayerId();

  const { urlQuery, setUrlQuery } = useUrlQuery<RequirementsUrlQueryFilter>();

  const request = useMemo(() => createListRequest(urlQuery), [urlQuery]);
  const queryRequirements =
    queriesRequirements.listPayeeRequirements.usePaginatedQuery(
      {
        ...request,
        page: { size: PAGE_SIZE },
        xWingspanAccount
      },
      { refetchOnMount: true }
    );

  const requirements = useMemo(
    () =>
      flatten(queryRequirements.data?.pages.map(page => page.data || []) || []),
    [queryRequirements.data?.pages]
  );

  const requirementsConfig = useRequirementsConfig({
    requirements,
    contextType: RequirementContextType.Payee
  });

  const requirementsListSize = useMemo(
    () => queryRequirements.data?.pages[0]?.pagination?.totalSize,
    [queryRequirements.data?.pages]
  );

  const bulkReviewProps = useMemo((): BulkReviewProps<PayeeRequirement> => {
    return {
      activeId: requirementId,
      listSize: requirementsListSize,
      rows:
        requirements.map(requirement => {
          const dataSourceContextType =
            requirementsConfig.getContextType(requirement);
          const dataSourcePolicyType =
            requirementsConfig.getPolicyType(requirement);

          return {
            id: requirement.id!,
            text: requirement.name || "–",
            secondaryText: requirement.payeeAccountId
              ? React.createElement(PayeeName, {
                  payeeId: requirement.payeeAccountId
                })
              : undefined,
            tertiaryText: getRequirementDefinitionName({
              type: requirement.type!,
              contextType: dataSourceContextType,
              policyType: dataSourcePolicyType
            }),
            tertiaryTextIcon: getRequirementDefinitionIcon({
              type: requirement.type!,
              contextType: dataSourceContextType,
              policyType: dataSourcePolicyType
            }),
            updatedAt: requirement.events?.createdAt,
            data: requirement
          };
        }) || [],
      onRowClick: row => {
        history.push({
          pathname: `/member/payees/${row.data.payeeAccountId}/requirement/${row.id}`,
          search: location.search
        });
      },
      fetchNextPage: queryRequirements.fetchNextPage,
      hasNextPage:
        !queryRequirements.isFetchingNextPage && queryRequirements.hasNextPage,
      onClose: () => {
        setUrlQuery({
          filter: {}
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on requirementsConfig.dataUpdatedAt, not the unstable config object
  }, [
    history,
    location.search,
    queryRequirements.fetchNextPage,
    queryRequirements.hasNextPage,
    queryRequirements.isFetchingNextPage,
    requirementId,
    requirements,
    requirementsListSize,
    setUrlQuery,
    requirementsConfig.dataUpdatedAt
  ]);

  // Handle bulk review visibility. Hide it on unmount
  useEffect(() => {
    const hasActiveFilters = Object.keys(urlQuery.filter).length > 0;
    const hasMultipleRequirements = requirements.length > 1;

    setStore({
      bulkReviewVisible: hasMultipleRequirements ? hasActiveFilters : false
    });

    return () => {
      setStore({
        bulkReviewVisible: false
      });
    };
  }, [requirements.length, setStore, urlQuery.filter]);

  const activeIndex = useMemo(
    () => requirements.findIndex(row => row.id === requirementId),
    [requirements, requirementId]
  );

  const prevRequirement = useMemo(() => {
    if (activeIndex > 0) {
      return requirements[activeIndex - 1];
    }
    return undefined;
  }, [activeIndex, requirements]);

  const nextRequirement = useMemo(() => {
    if (activeIndex < requirements.length - 1) {
      return requirements[activeIndex + 1];
    }
    return undefined;
  }, [activeIndex, requirements]);

  // Prefetch requirements
  queriesRequirements.getPayeeRequirement.useQuery(
    { requirementId: prevRequirement?.id!, xWingspanAccount },
    { enabled: !!prevRequirement, staleTime: Infinity }
  );
  queriesRequirements.getPayeeRequirement.useQuery(
    { requirementId: nextRequirement?.id!, xWingspanAccount },
    { enabled: !!nextRequirement, staleTime: Infinity }
  );

  // Prefetch payees into the v3 cache that `PayeeName` now reads.
  queriesPayees.getPayee.useQuery(
    { payeeId: prevRequirement?.payeeAccountId!, xWingspanAccount },
    { enabled: !!prevRequirement, staleTime: Infinity }
  );
  queriesPayees.getPayee.useQuery(
    { payeeId: nextRequirement?.payeeAccountId!, xWingspanAccount },
    { enabled: !!nextRequirement, staleTime: Infinity }
  );

  // Set bulk review data
  useEffect(() => {
    setStore({
      bulkReview: bulkReviewProps
    });
  }, [bulkReviewProps, setStore]);

  return {
    queryRequirements
  };
}
